/**
 * Calculates the freshness of a date (how long ago it was)
 * Returns the appropriate time unit and value for display
 */
export interface FreshnessResult {
  id: 'hit.lastSeen.minutesAgo' | 'hit.lastSeen.hoursAgo' | 'hit.lastSeen.daysAgo';
  value: number;
}

function parseLastSeenInput(lastSeen: string | number | undefined | null): Date | null {
  if (lastSeen === undefined || lastSeen === null) return null;

  if (typeof lastSeen === 'number' && Number.isFinite(lastSeen)) {
    return new Date(lastSeen < 10_000_000_000 ? lastSeen * 1000 : lastSeen);
  }

  const raw = String(lastSeen).trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return null;
    return new Date(numeric < 10_000_000_000 ? numeric * 1000 : numeric);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Calculates how fresh a date is (minutes, hours, or days ago)
 * 
 * @param lastSeen - ISO date string, unix seconds, or unix milliseconds
 * @returns Freshness result with id and value, or null if invalid
 */
export function calculateFreshness(lastSeen: string | number | undefined | null): FreshnessResult | null {
  try {
    const now = new Date();
    const date = parseLastSeenInput(lastSeen);
    if (!date || Number.isNaN(date.getTime())) return null;

    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) return null;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 120) {
      return {
        id: 'hit.lastSeen.minutesAgo',
        value: diffMins,
      };
    } else if (diffHours < 24) {
      return {
        id: 'hit.lastSeen.hoursAgo',
        value: diffHours,
      };
    } else {
      return {
        id: 'hit.lastSeen.daysAgo',
        value: diffDays,
      };
    }
  } catch {
    return null;
  }
}
